<template>
  <div class="energy-storage-status">
    <!-- 顶部控制栏 -->
    <div class="status-header">
      <div class="header-title">储能系统</div>

      <!-- 选择器 -->
      <div class="selector-group">
        <!-- 设备选择器 (Array/eLink) -->
        <v-select v-model="selectedArray" :items="arrayOptions" item-title="name" item-value="id" return-object
          variant="outlined" density="compact" rounded hide-details class="selector-select" placeholder="请选择"
          @update:modelValue="onArrayChange">
        </v-select>

        <!-- 指标选择器 (Status) -->
        <v-select v-model="selectedStatus" :items="statusOptions" variant="outlined" density="compact" rounded
          hide-details class="selector-select selector-wide" @update:modelValue="onStatusChange">
        </v-select>

        <div class="detail-btn" @click="openDetailDialog">
          <span class="btn-text">详情</span>
        </div>
      </div>
    </div>

    <!-- 储能系统详情弹框 -->
    <EnergyStorageDetailDialog v-model="showDetailDialog" />

    <!-- 主内容区域 -->
    <div class="status-content">
      <!-- 左侧：eLink 详细信息卡片 -->
      <div class="elink-card">
        <div class="card-overlay"></div>
        <div class="card-content">
          <v-tooltip :text="elinkRealtimeData?.elink_name" location="top">
            <template #activator="{ props }">
              <div v-bind="props" class="elink-name">
                {{ elinkRealtimeData?.elink_name || '暂无数据' }}
              </div>
            </template>
          </v-tooltip>
          <div class="elink-progress">
            <div class="progress-fill" :style="{ width: `${elinkRealtimeData?.soc || 0}%` }"></div>
          </div>
          <div class="elink-info">
            <div class="info-item">工作状态：{{ formatStatus(elinkRealtimeData?.run_st, ELinkWorkStatus) }}</div>
            <div class="info-item">实时功率：{{ formatValue(elinkRealtimeData?.p_total) }}<span
                v-if="elinkRealtimeData?.p_total != null">kW</span></div>
            <div class="info-item">SOC：{{ formatValue(elinkRealtimeData?.soc) }}<span
                v-if="elinkRealtimeData?.soc != null">%</span></div>
          </div>
          <div class="update-time">{{ formatUpdateTime() }}</div>
        </div>
      </div>

      <!-- 右侧：能量块状态网格 -->
      <div class="energy-blocks">
        <div class="blocks-grid">
          <div v-for="block in filteredEnergyBlocks" :key="block.id" class="block-item">
            <v-tooltip activator="parent" location="bottom" content-class="eblock-tooltip">
              <div class="tooltip-content" v-if="block.rawData">
                <div class="tooltip-row">
                  <span class="label">工作状态：</span>
                  <span class="value">{{ formatStatus(block.rawData?.state_eblock, EBlockWorkStatus) }}</span>
                </div>
                <div class="tooltip-row">
                  <span class="label">实时功率：</span>
                  <span class="value">{{ formatValue(block.rawData?.p_ac) }}<span
                      v-if="block.rawData?.p_ac != null">kW</span></span>
                </div>
                <div class="tooltip-row">
                  <span class="label">电压：</span>
                  <span class="value">{{ formatValue(block.rawData?.u_dc) }}<span
                      v-if="block.rawData?.u_dc != null">V</span></span>
                </div>
                <div class="tooltip-row">
                  <span class="label">电流：</span>
                  <span class="value">{{ formatValue(block.rawData?.i_dc) }}<span
                      v-if="block.rawData?.i_dc != null">A</span></span>
                </div>
                <div class="tooltip-row">
                  <span class="label">SOC：</span>
                  <span class="value">{{ formatValue(block.rawData?.soc) }}<span
                      v-if="block.rawData?.soc != null">%</span></span>
                </div>
              </div>
            </v-tooltip>
            <div class="block-header">
              <span class="block-name">{{ block.name.split('#')[0] }}#eb</span>
              <span class="block-status" :class="block.displayClass">{{ block.displayValue }}</span>
            </div>
            <div class="block-progress">
              <div class="block-progress-fill" :class="block.progressClass" :style="{ width: block.progress }"></div>
            </div>
          </div>
        </div>
        <div v-if="filteredEnergyBlocks.length === 0" class="empty-state">
          暂无数据
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { ws_req } from '@/ws/wss-request'
import useUserStore from '@/store/modules/user'
import EnergyStorageDetailDialog from './EnergyStorageDetailDialog.vue'
import {
  EBlockWorkStatus,
  ELinkWorkStatus,
  EBlockStatusClass,
  StatusIndicatorOptions,
  formatStatus
} from './monitorEnums'

// 格式化数值，区分 0 和 无数据
const formatValue = (val) => {
  if (val === null || val === undefined || val === '') return '--'
  return val
}

// 接收父组件传递的 eLink 数据
const props = defineProps({
  elinkData: {
    type: Array,
    default: () => []
  },
  stationId: {
    type: [String, Number],
    default: '1'
  }
})

// 下拉选择相关逻辑 - 第二个下拉框：指标选择
const statusOptions = StatusIndicatorOptions

const selectedArray = ref(null)
const selectedStatus = ref('充放电状态')



// 详情弹框
const showDetailDialog = ref(false)

// WebSocket 数据
const elinkRealtimeData = ref(null)
const eblockListData = ref([])
let wsTimer = null

const route = useRoute()
// const stationId = computed(() => route.query.stationId || route.params.stationId || '1')

// 将父组件传递的数据转换为下拉选项
const arrayOptions = computed(() => {
  return props.elinkData.map(item => ({
    id: item.equipmentId,
    name: item.equipmentName,
    uuid: item.uuid,
    sn: item.equipmentSN,
    children: item.children || []
  }))
})


// 监听 arrayOptions 变化，设置默认选中项
watch(arrayOptions, (newOptions) => {
  if (newOptions.length > 0) {
    // 总是重置为第一个，确保切换电站后状态更新
    selectedArray.value = newOptions[0]
  } else {
    selectedArray.value = null
    elinkRealtimeData.value = null
    eblockListData.value = []
  }
}, { immediate: true })

// 获取 eLink 和 eBlock 实时数据
const fetchElinkEblockData = async (recurs = true) => {
  clearTimeout(wsTimer)

  if (!selectedArray.value || !selectedArray.value.id) {
    return
  }

  try {
    // 检查连接状态，尝试自动恢复
    if (!ws_req.is_open && !ws_req.is_connecting) {
      const userStore = useUserStore()
      await userStore.getInfo()
    }

    const result = await ws_req.request('elink_eblock_brief', {
      stationId: Number(props.stationId),
      eLinkId: selectedArray.value.id
    })

    // 兼容不同的返回结构
    const data = result?.data || result
    if (data) {
      elinkRealtimeData.value = data.eLink || null
      eblockListData.value = data.eBlockList || []
    }
  } catch (e) {
    if (e.message && e.message.includes('WebSocket 未连接')) {
      console.warn('EnergyStorage WS not connected, retrying later...')
    } else {
      console.error('EnergyStorage WS request failed:', e)
    }
  }

  if (recurs) {
    wsTimer = setTimeout(() => {
      fetchElinkEblockData(true)
    }, 5000)
  }
}

// 监听选中的 eLink 变化，重新获取数据
watch(selectedArray, (newArray) => {
  if (newArray) {
    fetchElinkEblockData()
  }
}, { immediate: true })

// 根据指标选择获取显示值
const getDisplayValue = (block, indicator) => {
  switch (indicator) {
    case '充放电状态':
      return formatStatus(block.state_eblock, EBlockWorkStatus)
    case '功率':
      return `${block.p_ac || 0}kW`
    case '电压':
      return `${block.u_dc || 0}V`
    case '电流':
      return `${block.i_dc || 0}A`
    case 'SOC':
      return `${block.soc || 0}%`
    default:
      return formatStatus(block.state_eblock, EBlockWorkStatus)
  }
}

// 根据指标获取样式类
const getDisplayClass = (block, indicator) => {
  if (indicator === '充放电状态') {
    const status = formatStatus(block.state_eblock, EBlockWorkStatus)
    return EBlockStatusClass[status] || 'stopped'
  }
  return 'normal'
}

// 能量块列表
const filteredEnergyBlocks = computed(() => {
  if (!eblockListData.value || eblockListData.value.length === 0) {
    return []
  }

  return eblockListData.value.map(block => {
    const status = formatStatus(block.state_eblock, EBlockWorkStatus)
    return {
      id: block.eblock_id,
      name: block.eblock_name || `${block.eblock_id}#eb`,
      status: status,
      statusClass: EBlockStatusClass[status] || 'stopped',
      displayValue: getDisplayValue(block, selectedStatus.value),
      displayClass: getDisplayClass(block, selectedStatus.value),
      progress: `${block.soc || 0}%`,
      progressClass: EBlockStatusClass[status] || 'stopped',
      rawData: block
    }
  })
})



// 格式化更新时间
const formatUpdateTime = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}更新`
}

const onArrayChange = (item) => {
  // selectedArray is updated by v-model
  // no need to manually set it, just trigger updates if needed
  // watch(selectedArray) handles data fetching
}

const onStatusChange = (item) => {
  // selectedStatus is updated by v-model
}

// 打开详情弹框
const openDetailDialog = () => {
  showDetailDialog.value = true
}

onUnmounted(() => {
  clearTimeout(wsTimer)
})


</script>

<style scoped lang="scss">
.energy-storage-status {
  width: 100%;
  /* 改为100%以适应父容器 */
  background: rgba(255, 255, 255, 1);
  border-radius: 20px;
  overflow: hidden;
  flex-shrink: 0;
}

.status-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
}

.header-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 16px;
  color: rgba(46, 46, 46, 1);
  font-weight: 400;
  line-height: normal;
}

.selector-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selector-select {
  width: 130px !important;
  flex-shrink: 0;
}

.selector-wide {
  width: 140px !important;
}

/* 
 * Border styles for v-select 
 * Matching the style from StationSelector/index.vue and StationList/index.vue 
 */
:deep(.v-field__outline) {
  border-radius: 16px !important;
  border: 1px solid #D4D9E5 !important;

  >div {
    display: none !important;
  }
}

/* 
 * Text styles inside v-select 
 */
:deep(.v-field__input),
:deep(.v-select__selection-text) {
  font-size: 14px !important;
  line-height: 24px !important;
}

.detail-btn {

  display: flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 40px;
  border-radius: 20px;
  border: 1px solid rgba(224, 228, 231, 1);
  cursor: pointer;
}

.btn-text {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 14px;
  color: rgba(46, 46, 46, 1);
  font-weight: 400;
  line-height: normal;
}

.status-content {
  display: flex;
  gap: 18px;
  padding: 0 0 20px 20px;
}

.elink-card {
  position: relative;
  width: 22%;
  /* 按比例展示，约等于 160/750 */
  min-width: 160px;
  /* 允许压缩 */
  height: 200px;
  border-radius: 10px;
  border: 1px solid rgba(244, 247, 252, 1);
  overflow: hidden;
}

.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: rgba(239, 244, 251, 0.7);
  border-radius: 10px;
}

.card-content {
  position: relative;
  padding: 12px;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  /* 确保内容宽度自适应 */
}

.elink-name {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 14px;
  color: rgba(0, 0, 0, 1);
  font-weight: 500;
  line-height: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.elink-progress {
  margin-top: 12px;
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 1);
  border-radius: 20px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(270deg, rgba(141, 211, 245, 1) 5.43%, rgba(184, 229, 214, 1) 52.72%, rgba(222, 248, 179, 1) 100%);
  border-radius: 20px;
  transition: width 0.3s ease;
}

.elink-info {
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  overflow: hidden;
  /* 防止文字溢出 */
}

.info-item {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 12px;
  color: rgba(46, 46, 46, 1);
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
  /* 防止文字换行 */
  text-overflow: ellipsis;
  /* 超出部分显示省略号 */
  overflow: hidden;
}

.update-time {
  margin-top: auto;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 12px;
  color: rgba(46, 46, 46, 0.7);
  font-weight: 300;
  line-height: normal;
  white-space: nowrap;
  transform: scale(0.9);
  /* 稍微缩小一点以适应更窄的宽度 */
  transform-origin: left bottom;
}

.energy-blocks {
  flex: 1;
  background: rgba(255, 255, 255, 1);
  overflow-y: auto;
  /* 允许纵向滚动 */
  height: 250px;
  /* 保持原有高度作为可视区域 */
  padding-right: 20px;
}

.blocks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
  /* 自动填充列，最小118px */
  gap: 12px;
  width: 100%;
  height: auto;
}

.block-item {
  background: rgba(239, 244, 251, 0.5);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 64px;
}

.block-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.block-name {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 14px;
  color: rgba(46, 46, 46, 1);
  font-weight: 500;
  line-height: normal;
}

.block-status {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;

  &.charging {
    color: rgba(57, 216, 187, 1);
  }

  &.discharging {
    color: rgba(255, 183, 77, 1);
  }

  &.stopped {
    color: rgba(255, 138, 103, 1);
  }

  &.standby {
    color: rgba(158, 158, 158, 1);
  }

  &.derate {
    color: rgba(255, 152, 0, 1);
  }

  &.warning {
    color: rgba(255, 193, 7, 1);
  }

  &.fault {
    color: rgba(244, 67, 54, 1);
  }

  &.normal {
    color: rgba(46, 46, 46, 1);
  }
}

.block-progress {
  margin-top: 12px;
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 1);
  border-radius: 20px;
  overflow: hidden;
}

.block-progress-fill {
  height: 100%;
  border-radius: 20px;
  transition: width 0.3s ease;

  &.charging {
    background: linear-gradient(270deg, rgba(141, 211, 245, 1) 4.44%, rgba(184, 229, 214, 1) 52.22%, rgba(222, 248, 179, 1) 100%);
  }

  &.discharging {
    background: linear-gradient(270deg, rgba(255, 183, 77, 1) 0%, rgba(255, 213, 128, 1) 100%);
  }

  &.stopped {
    background: rgba(255, 138, 103, 1);
    border-radius: 100px;
  }

  &.standby {
    background: rgba(158, 158, 158, 0.5);
  }

  &.derate {
    background: linear-gradient(270deg, rgba(255, 152, 0, 1) 0%, rgba(255, 193, 0, 1) 100%);
  }

  &.warning {
    background: linear-gradient(270deg, rgba(255, 193, 7, 1) 0%, rgba(255, 224, 130, 1) 100%);
  }

  &.fault {
    background: rgba(244, 67, 54, 1);
  }
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(158, 158, 158, 1);
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 14px;
}
</style>

<style lang="scss">
.eblock-tooltip {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(224, 228, 231, 1);
  border-radius: 8px !important;
  padding: 12px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  opacity: 1 !important;

  .tooltip-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .tooltip-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 12px;
    line-height: normal;
    min-width: 120px;

    .label {
      color: rgba(46, 46, 46, 0.7);
    }

    .value {
      color: rgba(46, 46, 46, 1);
      font-weight: 500;
    }
  }
}
</style>
